import { useState, useEffect } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const SetScoreInput = ({ initialSets, homeName, awayName, onChange }) => {
    const { t } = useTranslation()
    const [sets, setSets] = useState(() => {
        if (Array.isArray(initialSets) && initialSets.length > 0) {
            return initialSets.map(s => ({
                home: s.home !== null ? String(s.home) : '',
                away: s.away !== null ? String(s.away) : ''
            }))
        }
        return [{ home: '', away: '' }]
    })

    useEffect(() => {
        const validSets = sets
            .filter(s => s.home !== '' && s.away !== '' && s.home !== null && s.away !== null && s.home !== undefined && s.away !== undefined)
            .map(s => {
                const homeVal = parseInt(s.home)
                const awayVal = parseInt(s.away)
                return {
                    home: isNaN(homeVal) ? null : homeVal,
                    away: isNaN(awayVal) ? null : awayVal
                }
            })
            .filter(s => s.home !== null && s.away !== null)

        let homeWins = 0
        let awayWins = 0
        validSets.forEach(s => {
            if (s.home > s.away) homeWins++
            if (s.away > s.home) awayWins++
        })

        onChange({
            set_scores: validSets,
            score_home: homeWins,
            score_away: awayWins
        })
    }, [sets])

    const updateSet = (index, field, value) => {
        const newSets = [...sets]
        newSets[index] = { ...newSets[index], [field]: value }
        setSets(newSets)
    }

    const addSet = () => {
        if (sets.length < 5) {
            setSets([...sets, { home: '', away: '' }])
        }
    }

    const removeSet = (index) => {
        if (sets.length > 1) {
            setSets(sets.filter((_, i) => i !== index))
        }
    }

    return (
        <div className="bg-gray-50/50 rounded-xl p-3 border border-gray-100">
            <div className="flex flex-col gap-3">
                {/* Header Row */}
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-24"></div> {/* Team name spacer */}
                    {sets.map((_, i) => (
                        <div key={i} className="flex-1 text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            {t('matches_page.set_number', { count: i + 1 })}
                        </div>
                    ))}
                    <div className="w-8"></div> {/* Actions spacer */}
                </div>

                {/* Home Row */}
                <div className="flex items-center gap-2">
                    <div className="w-24 text-xs font-bold text-gray-600 truncate text-right" title={homeName}>
                        {homeName || t('matches_page.home_label')}
                    </div>
                    {sets.map((set, i) => (
                        <input
                            key={`home-${i}`}
                            type="number"
                            inputMode="numeric"
                            className="flex-1 min-w-0 h-10 text-center text-lg font-black bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                            value={set.home}
                            onChange={(e) => updateSet(i, 'home', e.target.value)}
                        />
                    ))}
                    <div className="w-8"></div>
                </div>

                {/* Away Row */}
                <div className="flex items-center gap-2">
                    <div className="w-24 text-xs font-bold text-gray-600 truncate text-right" title={awayName}>
                        {awayName || t('matches_page.away_label')}
                    </div>
                    {sets.map((set, i) => (
                        <input
                            key={`away-${i}`}
                            type="number"
                            inputMode="numeric"
                            className="flex-1 min-w-0 h-10 text-center text-lg font-black bg-white border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                            value={set.away}
                            onChange={(e) => updateSet(i, 'away', e.target.value)}
                        />
                    ))}
                    <div className="w-8 flex justify-center">
                        {sets.length > 1 && (
                            <button
                                onClick={() => removeSet(sets.length - 1)}
                                className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Actions */}
                {sets.length < 5 && (
                    <button
                        onClick={addSet}
                        className="mt-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border-2 border-dashed border-blue-200 transition-all"
                    >
                        <Plus size={14} /> {t('matches_page.add_set_btn')}
                    </button>
                )}
            </div>
        </div>
    )
}
